// Shared AI utilities
import { getLibraryGrounding } from './libraryGrounding.js'
import { useStore, defaultSketchCode } from '../../store/useStore.js'

// Blank stub for fresh sketches (when default template is detected)
const BLANK_SKETCH_STUB = `//====================================================
    // This code was written on the p5js.ai IDE
    // Using the xelsed-alpha6-flash tuned model 
    // Visit the site and support Dyslexic Research
//===================================================
function setup() {
  createCanvas(windowWidth, windowHeight);
  // Tip: Use windowWidth/windowHeight for responsive canvas
}
function draw() {
  background(220);
}
function windowResized() {
  resizeCanvas(windowWidth, windowHeight);
  // Called when preview panel is resized
}`

// System prompt for p5.js coding assistant
export const SYSTEM_PROMPT = `You are an expert p5.js coding assistant with AGENTIC CAPABILITIES. You help users create and debug p5.js sketches while grounding explanations in official documentation and best practices.

PROJECT STRUCTURE:
The user's project has multiple files:
- sketch.js: Main p5.js code with setup() and draw() functions
- index.html: HTML file that loads p5.js library and links to sketch.js and style.css
- style.css: CSS for styling the canvas and page
- (Additional .js files): Helper code, classes, or utilities

IMPORTANT CDN PREFERENCES:
- **three.js**: Use r147 with global scripts (unpkg.com/three@0.147.0/...) - NO ES6 modules
- **Load order matters**: Load three.js main library before any addons
- Check the AVAILABLE LIBRARIES section below for exact CDN URLs

 WEBGL TEXT: In WEBGL mode, use loadFont() in preload() with a .ttf/.otf URL, then textFont(font) in setup().
 p5.sound: Use bundled addon from p5@1.11.3/lib/addons/p5.sound.min.js. Load AFTER p5.js.

 GOOGLE FONTS: NEVER use fonts.gstatic.com URLs! Use fontsource CDN instead:
   - Convert font name to slug: "Roboto"  "roboto", "Open Sans"  "open-sans"
   - URL pattern: https://unpkg.com/@fontsource/{slug}@latest/files/{slug}-latin-400-normal.woff

 TONE.JS PITFALLS:
   - Transport.loopEnd: Use valid notation like "1m" (1 measure), "4n" (quarter), NOT "1.5n"
   - For N steps at 8th notes: Tone.Transport.loopEnd = Tone.Time("8n").toSeconds() * numSteps

 EUCLIDEAN RHYTHM (correct algorithm):
   function generateEuclideanRhythm(beats, steps) {
     if (beats <= 0 || steps <= 0 || beats > steps) return Array(steps).fill(false);
     const pattern = Array(steps).fill(false);
     for (let i = 0; i < beats; i++) pattern[Math.floor(i * steps / beats)] = true;
     return pattern;
   }

 THREE.JS MEMORY: Always dispose when removing meshes:
   scene.remove(mesh);
   mesh.geometry.dispose();
   mesh.material.dispose();

 AGENTIC MODE (Enhanced Thinking):
You have access to these capabilities:
1. **extended_thinking** - Take time to think through complex problems before responding
2. **web_search_tool** - Search for current information, library versions, and techniques

IMPORTANT: Return complete code in markdown blocks with filenames. This is a p5.js WEB PROJECT (not Node.js):
- sketch.js: Must include setup() and draw() functions
- index.html: Include proper <script> tags for libraries and sketch.js
- style.css: Include CSS rules for canvas/page
Verify library URLs exist in AVAILABLE LIBRARIES before using. Be persistent until fully resolved.
The user will apply your code using the "Apply" button in the interface.

Remember, you are an agent - please keep going until the user's query is completely resolved, before ending your turn and yielding back to the user. Only finish once the task is complete and verified.

LIBRARY SYSTEM:
A comprehensive list of PREFERRED, ALLOWED, and DISCOURAGED libraries will be provided in your context.
- ALWAYS check the "AVAILABLE LIBRARIES" section before suggesting any library
- Suggest PREFERRED libraries first
- Only suggest ALLOWED libraries if preferred doesn't fit the use case
- NEVER suggest DISCOURAGED libraries - provide alternatives instead
- Include CDN links when suggesting libraries
- Explain WHY a library is recommended

Guidelines:
- Provide clear, concise explanations
- Write clean, well-commented p5.js code
- Follow p5.js best practices
- When citing APIs or behavior, include a markdown link to the authoritative reference (e.g., https://p5js.org/reference/#/p5/circle for the circle function)
- When providing code, wrap it in a code block and specify which file it goes in
- If the user's current code has issues or console errors, explain them
- Suggest improvements and creative ideas
- Be encouraging and educational
- Use agentic tools when appropriate for complex tasks
- Mention the target p5.js version when explaining compatibility concerns (version info provided in context)
- NEVER suggest libraries not in the AVAILABLE LIBRARIES list above

IMPORTANT: You can see ALL project files and console errors. Fix errors when present. Use extended thinking for complex tasks.`;

// Get the default sketch code from store for comparison
function getDefaultSketchCode() {
  return useStore.getState().files.find(f => f.name === 'sketch.js')?.content || ''
}

// Get or create a persistent device ID for job tracking
export function getOrCreateDeviceId() {
  try {
    let deviceId = localStorage.getItem('p5ai_device_id')
    if (!deviceId) {
      deviceId = typeof crypto !== 'undefined' && crypto.randomUUID 
        ? crypto.randomUUID() 
        : String(Date.now())
      localStorage.setItem('p5ai_device_id', deviceId)
    }
    return deviceId
  } catch (e) {
    // Safari tracking prevention - return ephemeral ID
    return String(Date.now())
  }
}

// Get current session ID from store
export function getSessionId() {
  try {
    return useStore.getState().sessionId || null
  } catch (e) {
    return null
  }
}

// Get user's API key from settings
export function getUserApiKey(provider) {
  try {
    const savedSettings = localStorage.getItem('ai_settings')
    const settings = savedSettings ? JSON.parse(savedSettings) : { useServerKeys: true }

    if (settings.useServerKeys) {
      return null // Use server keys
    }

    // Get provider-specific key
    if (provider === 'openai') {
      return settings.openaiKey
    } else if (provider === 'anthropic') {
      return settings.anthropicKey
    } else if (provider === 'kimi') {
      return settings.kimiKey
    } else if (provider === 'gemini') {
      return settings.geminiKey
    }

    return null
  } catch (error) {
    // Safari tracking prevention - default to server keys
    return null
  }
}

// Auto-correct model based on provider
export function getCorrectModel(provider, model) {
  // Fix model selection based on provider
  if (provider === 'anthropic' && model.includes('gpt')) {
    console.log('🔧 Auto-corrected model for Claude: claude-sonnet-4-20250514')
    return 'claude-sonnet-4-20250514'
  } else if (provider === 'kimi' && !model.includes('kimi')) {
    console.log('🔧 Auto-corrected model for Kimi: kimi-k2-turbo-preview')
    return 'kimi-k2-turbo-preview'
  } else if (provider === 'gemini' && model.includes('gpt')) {
    console.log('🔧 Auto-corrected model for Gemini: gemini-3-pro-preview')
    return 'gemini-3-pro-preview'
  } else if (provider === 'xelsed' && model.includes('gpt')) {
    console.log('🔧 Auto-corrected model for Xelsed: xelsed-alpha06-flash')
    return 'xelsed-alpha06-flash'
  }
  return model
}

// Clean messages to only include role and content
export function cleanMessages(messages) {
  return messages.map(msg => ({
    role: msg.role,
    content: msg.content
  }))
}

// Get Claude-specific options from settings
export function getClaudeOptions() {
  try {
    const savedSettings = localStorage.getItem('ai_settings')
    const settings = savedSettings ? JSON.parse(savedSettings) : {}
    return {
      useWebSearch: settings.claudeWebSearch || false,
      useExtendedThinking: settings.claudeExtendedThinking || false
    }
  } catch (error) {
    return { useWebSearch: false, useExtendedThinking: false }
  }
}

// Build context with all files, console errors, and library version
export function buildContext(allFiles, consoleErrors, p5Version) {
  // Check if user wants to include base64 binary data
  const includeBase64 = useStore.getState().includeBase64InContext
  
  let fileContext = `\n\n=== LIBRARY VERSION ===\np5.js version: ${p5Version}\nIMPORTANT: Use syntax and features compatible with p5.js ${p5Version}\n`
  
  fileContext += '\n\n=== PROJECT FILES ===\n'
  
  allFiles.forEach(file => {
    // Check if sketch.js matches the default template - use blank stub instead
    if (file.name === 'sketch.js' && defaultSketchCode && file.content === defaultSketchCode) {
      fileContext += `\n--- sketch.js ---\n\`\`\`js\n${BLANK_SKETCH_STUB}\n\`\`\`\n`
    }
    // Binary files (base64 images/audio)
    else if (file.content && (file.content.startsWith('data:image/') || file.content.startsWith('data:audio/'))) {
      const isAudio = file.content.startsWith('data:audio/') || 
                      file.name.endsWith('.mp3') || 
                      file.name.endsWith('.wav') || 
                      file.name.endsWith('.ogg')
      const loadFunc = isAudio ? 'loadSound' : 'loadImage'
      
      if (includeBase64) {
        // Include full base64 content for multimodal AI
        fileContext += `\n--- ${file.name} ---\n[Binary file - ${loadFunc}('${file.name}') in preload()]\nData URL: ${file.content}\n`
      } else {
        // Just list name so AI knows they exist (saves tokens)
        fileContext += `\n--- ${file.name} ---\n[Uploaded ${isAudio ? 'audio' : 'image'} file - use ${loadFunc}('${file.name}') in preload()]\n`
      }
    }
    // Normal files - include full content
    else {
      fileContext += `\n--- ${file.name} ---\n\`\`\`${file.type}\n${file.content}\n\`\`\`\n`
    }
  })

  // Add console errors if any
  if (consoleErrors && consoleErrors.length > 0) {
    fileContext += '\n\n=== RECENT CONSOLE ERRORS ===\n'
    consoleErrors.slice(-5).forEach(err => {
      fileContext += `- [${err.level.toUpperCase()}] ${err.message}\n`
    })
  }

  // Add library grounding
  fileContext += getLibraryGrounding()

  return fileContext
}
